import React, { useState } from 'react';

import { SwiperSlide } from 'swiper/react';
import { formatPropertyId } from '$utilities/data';
import { PropertyAreaText, PropertyPriceText } from '$partials/properties';
import { SimpleImage } from '$partials/media';
import Button from '$partials/ui/button';
import IconArrow from '$partials/icons/icon-arrow';
import TeamMemberEnquireDialog from '$partials/properties/enquire-dialog';
import { Carousel, CarouselWrapper, CarouselPagination } from '$partials/base/carousel';
/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/items.d.ts').PropertyListingItem}
 */
const PropertyListingItem = (props) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { isCarouselItem = false } = props;

    const details = (
        <div className='flex-1 flex gap-16 items-center type-style-eyebrow text-main-text-primary group-hover:text-main-brand-text'>
            {props.bedrooms && (
                <p className='shrink-0'>
                    {props.bedrooms} bed
                </p>
            )}
            {props.bathrooms && (
                <p className='shrink-0'>
                    {props.bathrooms} bath
                </p>
            )}
            {props.livingAreaMeters && (
                <p className='shrink-0'>
                    <PropertyAreaText
                        areaMeters={props.livingAreaMeters}
                        isOffMarket={props.isOffMarket} />
                </p>
            )}
        </div>
    );

    return (
        <>
            <div
                className={`expand-interaction group flex flex-col gap-16 w-full group ${props.className || ''} ${props.isOffMarket ? `min-h-[400px] p-16 ${isCarouselItem ? 'theme-light' : 'theme-light-alt'} h-full bg-main-background` : ''}`}>
                {/* Property Image */}
                {(props.listingImage && !props.isOffMarket && !(props?.carouselListingImages?.length > 0)) && (
                    <a
                        className='aspect-[3/2] w-full overflow-hidden rounded relative'
                        href={props.url}>
                        <SimpleImage
                            {...props.listingImage}
                            className='w-full h-full object-cover' />
                        <div className='absolute inset-0 opacity-0 group-hover:opacity-100 before:absolute before:inset-0 before:bg-[rgba(0,0,0,0.4)] before:mix-blend-saturation before:content-[""] after:absolute after:inset-0 after:bg-[rgba(30,37,165,0.5)] after:mix-blend-screen after:content-[""]' />

                        {(props.isHighlight) &&
                            <div className='absolute inset-0 before:absolute before:inset-0 before:bg-[rgba(0,0,0,0.4)] before:mix-blend-saturation before:content-[""] after:absolute after:inset-0 after:bg-[rgba(30,37,165,0.5)] after:mix-blend-screen after:content-[""]'>
                                <div className='absolute top-16 left-16 bg-brand-500 rounded-[50%] w-[28px] h-[28px]' />
                            </div>
                        }
                    </a>
                )}

                {((props?.carouselListingImages?.length > 0) && !props.isOffMarket) && (
                    <a
                        className='aspect-[3/2] w-full overflow-hidden rounded relative z-[2]'
                        href={props.url}>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1,
                                spaceBetween: 0,
                                allowTouchMove: true,
                                grabCursor: true,
                                nested: props?.isCarouselItem,
                            }}>
                            <CarouselWrapper>
                                {props?.carouselListingImages?.map((item) => {
                                    return (
                                        <SwiperSlide
                                            aria-roledescription='slide'
                                            className='select-none cursor-pointer w-full h-full relative'
                                            key={item.id}
                                            role='group'>
                                            <SimpleImage
                                                className='w-full h-full object-cover aspect-[3/2]'
                                                {...item.media?.image} />
                                        </SwiperSlide>
                                    );
                                })}

                                {props?.carouselListingImages?.length > 1 && (
                                    <CarouselPagination totalSlides={props.carouselListingImages.length} />
                                )}
                            </CarouselWrapper>
                        </Carousel>
                        <div className='pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 before:absolute before:inset-0 before:bg-[rgba(0,0,0,0.4)] before:mix-blend-saturation before:content-[""] after:absolute after:inset-0 after:bg-[rgba(30,37,165,0.5)] after:mix-blend-screen after:content-[""]' />

                    </a>
                )}

                {props.listingLabel && (
                    <div className='absolute top-8 right-8'>
                        <div className='bg-tag-main-background border border-tag-main-border rounded flex h-24 items-center justify-center px-8 py-8'>
                            <p className='type-style-eyebrow text-main-brand-text'>
                                {props.listingLabel}
                            </p>
                        </div>
                    </div>
                )}

                {/* Property Details */}
                <div className={`flex flex-col gap-16 w-full ${props.isOffMarket ? 'h-full' : ''}`}>
                    {/* displayAddress and Price */}
                    <div className='flex flex-col gap-y-4 w-full'>
                        <div className='flex gap-8 items-center w-full'>
                            {props.isOffMarket && details}

                            {!props.isOffMarket &&
                                <p className='hidden sm:block type-style-eyebrow flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-main-text-primary group-hover:text-main-brand-text'>
                                    {props.displayAddress}
                                </p>
                            }

                            <div className='flex flex-col h-20 justify-center shrink-0'>
                                <div className='bg-tag-main-background border border-tag-main-border rounded flex h-24 items-center justify-center px-8 py-8'>
                                    <p className='type-style-eyebrow text-main-brand-text'>
                                        {props.isOffMarket && 'Off market'}

                                        {!props.isOffMarket && (
                                            <PropertyPriceText
                                                isOffMarket={props.isOffMarket}
                                                pageType={props.__typename}
                                                price={props.price}
                                                priceCurrency={props.priceCurrency}
                                                priceRentPeriod={props.priceRentPeriod}
                                                priceTypeLabel={props.priceTypeLabel} />
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <p className='block sm:hidden mt-16 type-style-eyebrow flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-main-text-primary group-hover:text-main-brand-text'>
                            {props.displayAddress}
                        </p>

                        {/* Bed/Bath/livingAreaMeters Details */}
                        {!props.isOffMarket && details}
                    </div>

                    {/* title and Tags */}
                    <div className={`flex flex-col w-full pr-0 md:pr-40 flex-1 ${props.isOffMarket ? 'gap-72 sm:gap-24' : 'gap-24'}`}>
                        {(!props.isOffMarket && props.title) && (
                            isCarouselItem ? (
                                <h3 className='type-style-body-l text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text'>
                                    <a
                                        className='expand-interaction__action'
                                        href={props.url}>
                                        {props.title}
                                    </a>
                                </h3>
                            ) : (
                                <a
                                    className='expand-interaction__action type-style-body-l text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text'
                                    href={props.url}>
                                    {props.title}
                                </a>
                            )
                        )}
                        {(props.isOffMarket && props.title) && (
                            isCarouselItem ? (
                                <h3 className='type-style-3 text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text'>
                                    <button
                                        className='hover:cursor-pointer text-left w-full'
                                        onClick={() => setIsDialogOpen(true)}
                                        type='button'>
                                        {props.title}
                                    </button>
                                </h3>
                            ) : (
                                <span
                                    className='type-style-3 text-main-text-primary whitespace-pre-wrap group-hover:text-main-brand-text hover:cursor-pointer'
                                    onClick={() => setIsDialogOpen(true)}>
                                    {props.title}
                                </span>
                            )
                        )}
                        {(props.isOffMarket) && (
                            <Button
                                className='mt-auto w-full sm:w-max type-style-eyebrow button-secondary-filled inline-flex gap-10 items-center group-hover:text-main-brand-text'
                                onClick={() => setIsDialogOpen(true)}>
                                Enquire
                                <span aria-hidden={true}>
                                    <IconArrow className='h-10' />
                                </span>
                            </Button>
                        )}
                        {props?.priceTypeLabel && props.priceTypeLabel !== 'Price On Application' && props.priceTypeLabel !== 'From' && !props.isOffMarket && (
                            <div className='flex flex-col w-fit h-20 justify-center shrink-0'>
                                <div className='bg-tag-main-background border border-tag-main-border rounded flex h-24 items-center justify-center px-8 py-8'>
                                    <p className='type-style-eyebrow text-main-brand-text'>
                                        {props.priceTypeLabel}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {props.offMarketProperty && isDialogOpen && (
                <TeamMemberEnquireDialog
                    agent={props.offMarketProperty.primaryAgent}
                    closeDialog={() => setIsDialogOpen(false)}
                    externalId={props.externalId}
                    isOpen={isDialogOpen}
                    office={props.offMarketProperty.office}
                    propertyReferenceId={formatPropertyId(props.externalId, props.offMarketProperty.parent?.externalReferenceId)} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PropertyListingItem;
